import {
  ExperienceBookableItem,
  ExperienceId,
  ExperiencesPriceBreakdown,
  ExperiencesShopOptionRequest,
  ExperiencesShopResponse,
  ExperiencesShopReviewsRequest,
  ExperiencesShopReviewsResponse,
  IExperiencesPassengerCounts,
  OpaqueToken,
  TrackingProperties,
  TravelWalletOffer,
} from "redmond";
import * as H from "history";
import * as actionTypes from "./constants";

export interface IFetchExperiencesShop {
  type: actionTypes.FETCH_EXPERIENCES_SHOP;
  history: H.History;
  isDatelessEnabled?: boolean;
}

export const fetchExperiencesShop = (args: {
  history: H.History;
  isDatelessEnabled?: boolean;
}): IFetchExperiencesShop => ({
  type: actionTypes.FETCH_EXPERIENCES_SHOP,
  ...args,
});

export interface ISetExperiencesShopDetails {
  type: actionTypes.SET_EXPERIENCES_SHOP_DETAILS;
  response: ExperiencesShopResponse;
  activtyIndex: number;
}

export const setExperiencesShopDetails = (args: {
  response: ExperiencesShopResponse;
  activtyIndex: number;
}): ISetExperiencesShopDetails => ({
  type: actionTypes.SET_EXPERIENCES_SHOP_DETAILS,
  ...args,
});

export interface ISetExperiencesShopDetailsCallStateFailed {
  type: actionTypes.SET_EXPERIENCES_SHOP_CALL_FAILED;
}

export const setExperiencesShopDetailsCallStateFailed =
  (): ISetExperiencesShopDetailsCallStateFailed => ({
    type: actionTypes.SET_EXPERIENCES_SHOP_CALL_FAILED,
  });

export interface ISetSelectedExperience {
  type: actionTypes.SET_SELECTED_EXPERIENCE;
  experienceId: ExperienceId;
}

export const setSelectedExperience = (
  experienceId: ExperienceId,
): ISetSelectedExperience => ({
  type: actionTypes.SET_SELECTED_EXPERIENCE,
  experienceId,
});

export interface IFetchExperiencesShopOptions {
  type: actionTypes.FETCH_EXPERIENCES_SHOP_OPTIONS;
  request: ExperiencesShopOptionRequest;
}

export const fetchExperiencesShopOptions = (
  request: ExperiencesShopOptionRequest,
): IFetchExperiencesShopOptions => ({
  type: actionTypes.FETCH_EXPERIENCES_SHOP_OPTIONS,
  request,
});

export interface ISetExperiencesShopOptions {
  type: actionTypes.SET_EXPERIENCES_SHOP_OPTIONS;
  priceBreakdowns: ExperiencesPriceBreakdown[];
  shopOptionTrackingProperties: TrackingProperties;
  opaqueActivityToken?: OpaqueToken;
  bestTravelOffer?: TravelWalletOffer;
}

export const setExperiencesShopOptions = (
  priceBreakdowns: ExperiencesPriceBreakdown[],
  shopOptionTrackingProperties: TrackingProperties,
  opaqueActivityToken?: OpaqueToken,
  bestTravelOffer?: TravelWalletOffer,
): ISetExperiencesShopOptions => ({
  type: actionTypes.SET_EXPERIENCES_SHOP_OPTIONS,
  priceBreakdowns,
  shopOptionTrackingProperties,
  opaqueActivityToken,
  bestTravelOffer,
});

export interface ISetExperiencesShopOptionsCallStateFailed {
  type: actionTypes.SET_EXPERIENCES_SHOP_OPTIONS_CALL_FAILED;
}

export const setExperiencesShopOptionsCallStateFailed =
  (): ISetExperiencesShopOptionsCallStateFailed => ({
    type: actionTypes.SET_EXPERIENCES_SHOP_OPTIONS_CALL_FAILED,
  });

export interface IFetchExperiencesShopReviews {
  type: actionTypes.FETCH_EXPERIENCES_SHOP_REVIEWS;
  request: ExperiencesShopReviewsRequest;
}

export const fetchExperiencesShopReviews = (
  request: ExperiencesShopReviewsRequest,
): IFetchExperiencesShopReviews => ({
  type: actionTypes.FETCH_EXPERIENCES_SHOP_REVIEWS,
  request,
});

export interface ISetExperiencesShopReviews {
  type: actionTypes.SET_EXPERIENCES_SHOP_REVIEWS;
  response: ExperiencesShopReviewsResponse;
}

export const setExperiencesShopReviews = (
  response: ExperiencesShopReviewsResponse,
): ISetExperiencesShopReviews => ({
  type: actionTypes.SET_EXPERIENCES_SHOP_REVIEWS,
  response,
});

export interface ISetExperiencesShopReviewsCallStateFailed {
  type: actionTypes.SET_EXPERIENCES_SHOP_REVIEWS_CALL_STATE_FAILED;
}

export const setExperiencesShopReviewsCallStateFailed =
  (): ISetExperiencesShopReviewsCallStateFailed => ({
    type: actionTypes.SET_EXPERIENCES_SHOP_REVIEWS_CALL_STATE_FAILED,
  });

export interface ISetTravelerCounts {
  type: actionTypes.SET_TRAVELER_COUNTS;
  counts: IExperiencesPassengerCounts;
}

export const setTravelerCounts = (
  counts: IExperiencesPassengerCounts,
): ISetTravelerCounts => ({
  type: actionTypes.SET_TRAVELER_COUNTS,
  counts,
});

export interface ISetSelectedTime {
  type: actionTypes.SET_SELECTED_TIME;
  selectedTime: string;
}

export const setSelectedTime = (selectedTime: string): ISetSelectedTime => ({
  type: actionTypes.SET_SELECTED_TIME,
  selectedTime,
});

export interface ISetSelectedDate {
  type: actionTypes.SET_SELECTED_DATE;
  selectedDate: string | null;
}

export const setSelectedDate = (selectedDate: string | null): ISetSelectedDate => ({
  type: actionTypes.SET_SELECTED_DATE,
  selectedDate,
});

export interface ISetSelectedBookableItem {
  type: actionTypes.SET_SELECTED_BOOKABLE_ITEM;
  selectedBookableItem: ExperienceBookableItem | null;
}

export const setSelectedBookableItem = (
  selectedBookableItem: ExperienceBookableItem | null,
): ISetSelectedBookableItem => ({
  type: actionTypes.SET_SELECTED_BOOKABLE_ITEM,
  selectedBookableItem,
});

export interface IFindNextAvailableDate {
  type: actionTypes.FIND_NEXT_AVAILABLE_DATE;
  candidateDates: string[];
  request: Omit<ExperiencesShopOptionRequest, "date">;
}

export const findNextAvailableDate = (
  candidateDates: string[],
  request: Omit<ExperiencesShopOptionRequest, "date">,
): IFindNextAvailableDate => ({
  type: actionTypes.FIND_NEXT_AVAILABLE_DATE,
  candidateDates,
  request,
});

export interface ISetNextAvailableDate {
  type: actionTypes.SET_NEXT_AVAILABLE_DATE;
  nextAvailableDate: string | null;
}

export const setNextAvailableDate = (
  nextAvailableDate: string | null,
): ISetNextAvailableDate => ({
  type: actionTypes.SET_NEXT_AVAILABLE_DATE,
  nextAvailableDate,
});

export type ExperiencesShopActions =
  | IFetchExperiencesShop
  | ISetExperiencesShopDetails
  | ISetExperiencesShopDetailsCallStateFailed
  | ISetSelectedExperience
  | IFetchExperiencesShopOptions
  | ISetExperiencesShopOptions
  | ISetExperiencesShopOptionsCallStateFailed
  | IFetchExperiencesShopReviews
  | ISetExperiencesShopReviews
  | ISetExperiencesShopReviewsCallStateFailed
  | ISetTravelerCounts
  | ISetSelectedTime
  | ISetSelectedDate
  | ISetSelectedBookableItem
  | IFindNextAvailableDate
  | ISetNextAvailableDate;
